import { TICK_MS } from "../movement/step.ts";

/**
 * The render clock remote entities are sampled at (CLIENT.md section 4, ADR-032's amendment of 2026-10-03): ticks,
 * fractional, which the interpolator samples `RENDER_DELAY_TICKS` behind. Each frame counts its time at a rate within
 * `CLOCK_STEER` of real time, steered in proportion to how far the clock is from its target, the newest tick less the
 * measured arrival jitter; it snaps forward when it trails the target by more than `CLOCK_SNAP_TICKS` and never runs
 * backwards. The jitter is the spread of the arrival offsets (arrival time less the tick's own time) of the last
 * `JITTER_WINDOW_TICKS` newer ticks, kept in a preallocated ring.
 */

/**
 * The most the clock's rate departs from real time while it steers, as a fraction.
 */
export const CLOCK_STEER = 0.1;

/**
 * How much the rate departs from real time per tick of distance from the target, before the `CLOCK_STEER` cap.
 */
export const CLOCK_GAIN = 0.1;

/**
 * How far the clock may trail its target, in ticks, before it snaps forward to it.
 */
export const CLOCK_SNAP_TICKS = 8;

/**
 * The most of one frame's time the clock counts, in ms, so a frame after a hidden tab does not carry it past its
 * target.
 */
export const CLOCK_MAX_FRAME_MS = 250;

/**
 * How many of the newest ticks' arrivals the jitter is measured over (2 s).
 */
export const JITTER_WINDOW_TICKS = 40;

/**
 * The most jitter the clock allows for, in ticks.
 */
export const JITTER_CAP_TICKS = 6;

/**
 * The clock, its newest tick and the offsets its jitter is measured from.
 */
export class RenderClock {
  private readonly offsets = new Float64Array(JITTER_WINDOW_TICKS);
  private count = 0;
  private next = 0;
  private clock: number;
  private newestTick: number;

  /**
   * @param tick The tick the clock starts at, the welcome's.
   */
  public constructor(tick: number) {
    this.clock = tick;
    this.newestTick = tick;
  }

  /**
   * The clock, in ticks.
   *
   * @returns The fractional tick.
   */
  public get ticks(): number {
    return this.clock;
  }

  /**
   * The newest tick observed.
   *
   * @returns The tick.
   */
  public get newest(): number {
    return this.newestTick;
  }

  /**
   * The measured jitter: the spread of the window's arrival offsets, in ticks, capped at `JITTER_CAP_TICKS`.
   *
   * @returns The jitter, 0 before two arrivals.
   */
  public get jitter(): number {
    if (this.count === 0) {
      return 0;
    }
    return Math.min(JITTER_CAP_TICKS, (this.largest() - this.smallest()) / TICK_MS);
  }

  /**
   * Restarts the clock at a tick and forgets every arrival, as a session begins.
   *
   * @param tick The tick.
   */
  public reset(tick: number): void {
    this.clock = tick;
    this.newestTick = tick;
    this.count = 0;
    this.next = 0;
  }

  /**
   * Records a tick's arrival. A tick no newer than the newest is ignored.
   *
   * @param tick The tick applied.
   * @param nowMs When it arrived, in ms on the clock `advance` is given.
   */
  public observe(tick: number, nowMs: number): void {
    if (tick <= this.newestTick && this.count > 0) {
      return;
    }
    this.newestTick = Math.max(this.newestTick, tick);
    this.offsets[this.next] = nowMs - tick * TICK_MS;
    this.next = (this.next + 1) % JITTER_WINDOW_TICKS;
    this.count = Math.min(JITTER_WINDOW_TICKS, this.count + 1);
  }

  /**
   * The tick the clock steers toward at a time: the newest tick the window's fastest arrival would have delivered by
   * then, less the jitter, which is the tick even the window's slowest arrival has delivered.
   *
   * @param nowMs The time, in ms.
   * @returns The target in ticks, or the clock itself before any arrival.
   */
  public target(nowMs: number): number {
    if (this.count === 0) {
      return this.clock;
    }
    return (nowMs - this.smallest()) / TICK_MS - this.jitter;
  }

  /**
   * Counts a frame: at most `CLOCK_MAX_FRAME_MS` of its time, at a rate of 1 plus `CLOCK_GAIN` per tick the clock
   * would trail its target after the frame at real time (minus per tick it would lead), held within `CLOCK_STEER`; a
   * clock left more than `CLOCK_SNAP_TICKS` behind its target then snaps forward to it. Before any arrival it counts
   * the frame at real time.
   *
   * @param deltaMs The frame's time, in ms.
   * @param nowMs The time at the frame's end, in ms.
   * @returns The clock after the frame, in ticks.
   */
  public advance(deltaMs: number, nowMs: number): number {
    const frame = Math.min(Math.max(deltaMs, 0), CLOCK_MAX_FRAME_MS) / TICK_MS;
    if (this.count === 0) {
      this.clock += frame;
      return this.clock;
    }
    const target = this.target(nowMs);
    const rate = 1 + Math.max(-CLOCK_STEER, Math.min(CLOCK_STEER, (target - this.clock - frame) * CLOCK_GAIN));
    this.clock += frame * rate;
    if (target - this.clock > CLOCK_SNAP_TICKS) {
      this.clock = target;
    }
    return this.clock;
  }

  /**
   * The largest offset in the window.
   *
   * @returns The offset in ms.
   */
  private largest(): number {
    let value = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < this.count; i += 1) {
      value = Math.max(value, this.offsets[i] as number);
    }
    return value;
  }

  /**
   * The smallest offset in the window.
   *
   * @returns The offset in ms.
   */
  private smallest(): number {
    let value = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.count; i += 1) {
      value = Math.min(value, this.offsets[i] as number);
    }
    return value;
  }
}
